import { v4 as uuid } from 'uuid';

import { getContext } from 'configs/context';

import type { IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import { BATCH_ACTIONS, BATCH_TOOLS, EXPORT_DESTINATIONS, BATCH_MINIAPP_ID } from 'pages/Batch/configs';

import analytics from 'services/analytics/index';
import ANALYTICS_EVENTS from 'services/analytics/config';

export enum SubToolNames {
  paddingColor = 'padding_color',
  usePadding = 'is_padding_used',
  socialSize = 'social_size',
  intensity = 'intensity',
  resizeBy = 'resize_by',
  position = 'position',
  texture = 'texture',
  opacity = 'opacity',
  eraser = 'eraser',
  rotate = 'rotate',
  color = 'color',
  crop = 'crop',
  size = 'size',
  myBg = 'my_bg',
  suggested = 'suggested',
  centerObject = 'center_object',
}

export interface ISubAction {
  name: SubToolNames | string;
  value?: string | number | boolean | Record<string, string> | Record<string, number>;
}

const ADJUST_HSL_SLOTS = 8;

const avgNonZero = (values: number[]): number => {
  const nonZero = values.filter(v => v !== 0);
  if (nonZero.length === 0) return 0;
  const sum = nonZero.reduce((acc, v) => acc + v, 0);
  return Math.round((sum / nonZero.length) * 100) / 100;
};

export const WATERMARK_ELEMENT_SUBTOOL = '__watermark_element__';

const WATERMARK_SETTING_NAMES = new Set<string>([SubToolNames.position, SubToolNames.opacity, SubToolNames.size, SubToolNames.rotate]);

export function buildWatermarkSubTools(subTools: ISubAction[]): ISubAction[] {
  const result: ISubAction[] = [];

  const element = subTools.find(s => s.name === WATERMARK_ELEMENT_SUBTOOL);
  if (element && element.value && typeof element.value === 'object') {
    const { category, item } = element.value as Record<string, string>;
    result.push({ name: category, value: item });
  }

  const settings = subTools
    .filter(entry => typeof entry.name === 'string' && WATERMARK_SETTING_NAMES.has(entry.name) && entry.value !== undefined)
    .reduce<Record<string, string>>((acc, entry) => {
      acc[entry.name as string] = String(entry.value);
      return acc;
    }, {});

  if (Object.keys(settings).length > 0) {
    result.push({ name: 'settings', value: settings });
  }

  return result;
}

export function buildCropSubTools(subTools: ISubAction[]): ISubAction[] {
  const byName = new Map<string, ISubAction['value']>(subTools.map(s => [s.name as string, s.value]));
  const result: ISubAction[] = [];

  const crop = byName.get(SubToolNames.crop);
  if (crop !== undefined) {
    result.push({ name: SubToolNames.crop, value: crop as string });
  }

  const rotate = byName.get(SubToolNames.rotate);
  if (rotate !== undefined) {
    result.push({ name: SubToolNames.rotate, value: rotate as string });
  }

  return result;
}

export function buildResizeSubTools(subTools: ISubAction[], activeColor: string | null): ISubAction[] {
  const byName = new Map<string, ISubAction['value']>(subTools.map(s => [s.name as string, s.value]));
  const result: ISubAction[] = [];

  result.push({
    name: SubToolNames.usePadding,
    value: byName.has(SubToolNames.usePadding) ? Boolean(byName.get(SubToolNames.usePadding)) : false,
  });

  const explicitColor = byName.get(SubToolNames.paddingColor);
  if (explicitColor !== undefined) {
    result.push({ name: SubToolNames.paddingColor, value: explicitColor as string });
  } else if (activeColor) {
    result.push({ name: SubToolNames.paddingColor, value: activeColor });
  }

  const socialSize = byName.get(SubToolNames.socialSize);
  const rawResizeBy = byName.get(SubToolNames.resizeBy);
  const resizeBy = socialSize !== undefined ? 'socials' : typeof rawResizeBy === 'string' ? rawResizeBy.toLowerCase() : undefined;
  if (resizeBy !== undefined) {
    result.push({ name: SubToolNames.resizeBy, value: resizeBy });
  }

  if (socialSize !== undefined) {
    result.push({ name: SubToolNames.socialSize, value: socialSize as string });
  }

  return result;
}

export function buildAdjustSubTools(params: Record<string, number>): ISubAction[] {
  const hslHues = Array.from({ length: ADJUST_HSL_SLOTS }, (_, i) => params[`hue${i}`] ?? 0);
  const hslSaturations = Array.from({ length: ADJUST_HSL_SLOTS }, (_, i) => params[`saturation${i}`] ?? 0);
  const hslLightnesses = Array.from({ length: ADJUST_HSL_SLOTS }, (_, i) => params[`lightness${i}`] ?? 0);

  return [
    {
      name: 'light',
      value: {
        brightness: params.brightness ?? 0,
        contrast: params.contrast ?? 0,
        shadows: params.shadows ?? 0,
        highlight: params.highlights ?? 0,
      },
    },
    {
      name: 'colors',
      value: {
        saturation: params.saturation ?? 0,
        hue: params.hue ?? 0,
        temperature: params.temperature ?? 0,
      },
    },
    {
      name: 'details',
      value: {
        clarity: params.clarity ?? 0,
      },
    },
    {
      name: 'hsl',
      value: {
        hue: avgNonZero(hslHues),
        saturation: avgNonZero(hslSaturations),
        lightness: avgNonZero(hslLightnesses),
      },
    },
  ];
}

export interface IAppliedSubTools {
  parent: BATCH_TOOLS;
  action: ISubAction;
}

export interface IAppliedTools {
  isPremium: boolean;
  parent: BATCH_TOOLS;
  [key: string]: string | number | boolean;
}

export enum OriginEnum {
  batch_editor = 'batch_editor',
  background_chooser = 'background_chooser',
}

export enum ItemSourceEnum {
  picsart_drive = 'picsart_drive',
  google_drive = 'google_drive',
  device = 'device',
}

export interface IPresetSettings {
  preset_category: string;
  preset_id: string;
  preset_name: string;
  preset_tools: string;
}

const toolAnalyticsNameMap: Partial<Record<BATCH_TOOLS, string>> = {
  [BATCH_TOOLS.watermark]: 'elements',
  [BATCH_TOOLS.wfx]: 'effects',
  [BATCH_TOOLS.background]: 'remove_background',
  [BATCH_TOOLS.aiBG]: 'remove_background',
};

export const getToolAnalyticsName = (tool: BATCH_TOOLS | 'erase' | string): string => {
  if (tool in toolAnalyticsNameMap) {
    return toolAnalyticsNameMap[tool as BATCH_TOOLS] as string;
  }
  return tool as string;
};

const batchActionToToolMap: Partial<Record<BATCH_ACTIONS, BATCH_TOOLS>> = {
  [BATCH_ACTIONS.removeBackground]: BATCH_TOOLS.background,
  [BATCH_ACTIONS.applyBackground]: BATCH_TOOLS.background,
  [BATCH_ACTIONS.generateAIBackground]: BATCH_TOOLS.background,
  [BATCH_ACTIONS.applyEnhance]: BATCH_TOOLS.enhance,
  [BATCH_ACTIONS.aiEnhance]: BATCH_TOOLS.enhance,
  [BATCH_ACTIONS.composedEnhance]: BATCH_TOOLS.enhance,
  [BATCH_ACTIONS.applyCrop]: BATCH_TOOLS.crop,
  [BATCH_ACTIONS.watermark]: BATCH_TOOLS.watermark,
  [BATCH_ACTIONS.resize]: BATCH_TOOLS.resize,
  [BATCH_ACTIONS.wfx]: BATCH_TOOLS.wfx,
  [BATCH_ACTIONS.applyWfx]: BATCH_TOOLS.wfx,
  [BATCH_ACTIONS.adjust]: BATCH_TOOLS.adjust,
  [BATCH_ACTIONS.applyAdjust]: BATCH_TOOLS.adjust,
};

export const getToolFromBatchAction = (action: BATCH_ACTIONS): BATCH_TOOLS | undefined => batchActionToToolMap[action];

const imageFormatMapper: Record<string, string> = {
  'image/jpeg': 'jpeg',
  'image/webp': 'webp',
  'image/png': 'png',
  'image/jpg': 'jpg',
};
const sidSource = uuid();

class BatchAnalytics {
  private _packageId = BATCH_MINIAPP_ID;

  private _sidSource = sidSource;

  private _screenName = 'batch_editor';

  private _appliedTools: IAppliedTools[] = [];

  private _appliedSubTools: IAppliedSubTools[] = [];

  utils() {
    return {
      setSubTool: this.setSubTool.bind(this),
      getSubTools: this.getSubTools.bind(this),
      resetSubTool: this.resetSubTool.bind(this),
    };
  }

  private setSubTool(parent: BATCH_TOOLS, action: ISubAction, resetParentSubs: boolean | SubToolNames = false) {
    const replacementSubIdx = this._appliedSubTools.findIndex(x => {
      const shouldOverrideSub = typeof resetParentSubs === 'boolean' ? resetParentSubs : resetParentSubs === x.action.name;
      return x.parent === parent && (shouldOverrideSub || x.action.name === action.name);
    });

    const newSubTool = { parent, action };

    if (replacementSubIdx > -1) {
      this._appliedSubTools[replacementSubIdx] = newSubTool;
    } else {
      this._appliedSubTools.push(newSubTool);
    }
  }

  private getSubTools() {
    return this._appliedSubTools.map(x => x.action);
  }

  private resetSubTool() {
    this._appliedSubTools = [];
  }

  // ------------------ Old Events ------------------ //

  openTool(type: BATCH_TOOLS | 'erase', all: boolean) {
    const itemSource = all ? 'all_items' : 'selected_items';
    analytics.sendEvent(ANALYTICS_EVENTS.PHOTO_EDIT_ITEM_OPEN, {
      on_item: itemSource,
      source: this._screenName,
      item: getToolAnalyticsName(type),
    });
  }

  toolExit(type: BATCH_TOOLS, action: 'apply' | 'cancel' | 'preset_apply', itemCount = 1, overrideSubTools?: ISubAction[], overridePerItem?: ISubAction[][]) {
    const subTools = overridePerItem?.[0] ?? overrideSubTools ?? this.getSubTools();
    analytics.sendEvent(ANALYTICS_EVENTS.PHOTO_EDIT_ITEM_ACTION, {
      source: this._screenName,
      applied_sub_tools: subTools,
      item: getToolAnalyticsName(type),
      item_count: Math.max(itemCount, 1),
      action,
    });

    this.resetSubTool();
  }

  presetApply(item: string, itemCount = 1, appliedSubTools: ISubAction[] = []) {
    analytics.sendEvent(ANALYTICS_EVENTS.PHOTO_EDIT_ITEM_ACTION, {
      source: this._screenName,
      item,
      item_count: Math.max(itemCount, 1),
      ...(appliedSubTools.length > 0 && { applied_sub_tools: appliedSubTools }),
      action: 'preset_apply',
    });
  }

  objectClick(action: 'click' | 'upload', params?: any) {
    analytics.sendEvent(ANALYTICS_EVENTS.OBJECT_CLICK, {
      source: this._screenName,
      origin: this._screenName,
      item: 'background',
      action,
      ...params,
    });
  }

  objectTry(params?: any) {
    analytics.sendEvent(ANALYTICS_EVENTS.OBJECT_TRY, {
      source: this._screenName,
      origin: this._screenName,
      ...params,
    });
  }

  // ------------------ New Events ------------------ //

  applyTool(tool: IAppliedTools) {
    this._appliedTools = [...this._appliedTools, tool];
  }

  clickEvent(item: string, extra?: { item_count?: number; settings?: IPresetSettings[] }) {
    analytics.sendEvent(ANALYTICS_EVENTS.PAGE_CLICK, {
      screen_name: this._screenName,
      source: this._screenName,
      action_button: getToolAnalyticsName(item),
      ...(extra?.item_count !== undefined && { item_count: extra.item_count }),
      ...(extra?.settings && { settings: extra.settings }),
    });
  }

  pageClick(params?: any) {
    analytics.sendEvent(ANALYTICS_EVENTS.PAGE_CLICK, params);
  }

  uploadDone(
    origin?: OriginEnum,
    upload_type?: string,
    result?: 'success' | 'partial_upload' | 'fail' | 'cancel',
    count = 0,
    isDrop = false,
    extra?: { failure_reason?: string; item_source?: ItemSourceEnum | string },
  ) {
    analytics.sendEvent(ANALYTICS_EVENTS.UPLOAD_DONE, {
      ...(count && {
        item_count: count,
      }),
      ...(result !== 'cancel' && {
        upload_type: upload_type || (count > 1 ? 'batch_upload' : 'single_upload'),
      }),
      origin,
      item: 'image',
      result,
      braze: true,
      ...(result !== 'cancel' ? { is_drag_and_drop: isDrop } : {}),
      ...(extra?.failure_reason && { failure_reason: extra.failure_reason }),
      ...(extra?.item_source && { item_source: extra.item_source }),
    });
  }

  exportOpen() {
    const { isWeb } = getContext().handlers;

    if (!isWeb()) return;

    analytics.sendEvent(ANALYTICS_EVENTS.EXPORT_POPUP_OPEN, {
      is_subscribed: getContext().general.auth.getState().isSubscribed,
      origin: this._screenName,
      media_type: 'image',
      source: 'download',
    });
  }

  exportAction(actionType: string, format?: IMAGE_MIME_TYPES) {
    const { isWeb } = getContext().handlers;

    if (!isWeb()) return;

    analytics.sendEvent(ANALYTICS_EVENTS.EXPORT_POPUP_ACTION, {
      ...(format && { format: imageFormatMapper[format] }),
      origin: this._screenName,
      action_type: actionType,
      media_type: 'image',
      source: 'download',
    });
  }

  download(count: number, format?: IMAGE_MIME_TYPES, destination?: EXPORT_DESTINATIONS) {
    if (!count) return;
    const { isWeb } = getContext().handlers;
    const activDestination = destination === EXPORT_DESTINATIONS.DEVICE ? 'local' : 'picsart_drive';
    const mappedAppliedTools = this._appliedTools.map(tool => ({
      ...tool,
      parent: getToolAnalyticsName(tool.parent),
    }));

    if (isWeb()) {
      analytics.sendEvent(ANALYTICS_EVENTS.OBJECT_EXPORT, {
        item: 'batch_items',
        item_count: count,
        origin: this._screenName,
        destination: activDestination,
        source: this._packageId,
        tool_names: Array.from(new Set(mappedAppliedTools.map(tool => tool.parent))),
        is_premium: Array.from(
          new Set(
            mappedAppliedTools.map(tool => ({
              parent: tool.parent,
              premium: tool.isPremium,
            })),
          ),
        ),
      });
    } else {
      analytics.sendEvent(ANALYTICS_EVENTS.OBJECT_EXPORT, {
        ...(format && { format: imageFormatMapper[format as string] }),
        destination: activDestination,
        origin: this._screenName,
        source: this._packageId,
        source_sid: this._sidSource,
      });

      analytics.sendEvent(ANALYTICS_EVENTS.BATCH_EXPORT, {
        source: this._screenName,
        settings: JSON.stringify({
          tools_used: mappedAppliedTools,
          is_premium: Array.from(
            new Set(
              mappedAppliedTools.map(tool => ({
                parent: tool.parent,
                premium: tool.isPremium,
              })),
            ),
          ),
          batch_size: count,
        }),
      });
    }
  }

  subscriptionOfferOpen() {
    const { isWeb } = getContext().handlers;

    if (!isWeb()) {
      analytics.sendEvent(ANALYTICS_EVENTS.SUBSCRIPTION_OFFER_OPEN, {
        source: this._packageId,
      });
    }
  }

  openPopup(type?: string, tool?: BATCH_TOOLS | null) {
    const { isWeb } = getContext().handlers;

    if (isWeb()) {
      analytics.sendEvent(ANALYTICS_EVENTS.POPUP_OPEN, {
        source: `${this._screenName}_${tool || 'unknown'}`,
        name: type,
      });
    } else {
      analytics.sendEvent(ANALYTICS_EVENTS.POPUP_OPEN, {
        origin: this._screenName,
        source: 'download',
        media_type: 'image',
        is_subscribed: getContext().general.auth.getState().isSubscribed,
      });
    }
  }

  registrationOpen() {
    analytics.sendEvent(ANALYTICS_EVENTS.REGISTRATION_OPEN, {
      action: 'click',
      source: this._packageId,
      action_button: this._packageId,
    });
  }
}

export default new BatchAnalytics();
